import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { 
  ArrowLeft, 
  Calendar, 
  User, 
  MessageCircle, 
  Paperclip, 
  ExternalLink,
  Edit,
  Trash2,
  Flag,
  Share2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { cn } from '@/lib/utils';
import { FeedbackItemWithDetails } from '@/types/feedback';
import { VotingComponent } from '@/components/feedback';

interface FeedbackDetailProps {}

const FeedbackDetail: React.FC<FeedbackDetailProps> = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const { toast } = useToast();

  const [feedback, setFeedback] = useState<FeedbackItemWithDetails | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (slug) {
      loadFeedback();
    }
  }, [slug]);

  const loadFeedback = async () => {
    if (!slug) return;

    setIsLoading(true);
    setNotFound(false);

    try {
      // Load feedback with all details
      const { data, error } = await supabase
        .from('feedback_items')
        .select(`
          *,
          category:feedback_categories(*),
          status:feedback_statuses(*),
          author_profile:profiles!feedback_items_author_id_fkey(username, full_name, avatar_url)
        `)
        .eq('slug', slug)
        .eq('is_published', true)
        .single();

      if (error || !data) {
        setNotFound(true);
        return;
      }

      // Get user's vote if authenticated
      let userVote = 0;
      if (isAuthenticated) {
        const { data: voteData } = await supabase
          .from('feedback_votes')
          .select('value')
          .eq('feedback_id', data.id)
          .eq('user_id', user?.id)
          .single();

        if (voteData) {
          userVote = voteData.value;
        }
      }

      setFeedback({
        ...data,
        user_vote: userVote,
      });
    } catch (error: any) {
      console.error('Failed to load feedback:', error);
      toast({
        title: 'Failed to load feedback',
        description: 'Please try again',
        variant: 'destructive',
      });
      setNotFound(true);
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getInitials = (name: string | undefined) => {
    if (!name) return 'U';
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: feedback?.title,
          text: feedback?.description,
          url: window.location.href,
        });
      } catch (error) {
        // User cancelled or error occurred
      }
    } else {
      // Fallback to clipboard
      await navigator.clipboard.writeText(window.location.href);
      toast({
        title: 'Link copied',
        description: 'The feedback link has been copied to your clipboard',
      });
    }
  };

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto space-y-6">
          {/* Back button skeleton */}
          <Skeleton className="h-10 w-32" />
          
          {/* Header skeleton */}
          <Card>
            <CardHeader>
              <div className="flex items-start gap-4">
                <div className="flex flex-col items-center gap-2">
                  <Skeleton className="h-8 w-8 rounded" />
                  <Skeleton className="h-6 w-8" />
                  <Skeleton className="h-8 w-8 rounded" />
                </div>
                <div className="flex-1 space-y-3">
                  <div className="flex gap-2">
                    <Skeleton className="h-6 w-24" />
                    <Skeleton className="h-6 w-20" />
                  </div>
                  <Skeleton className="h-8 w-3/4" />
                  <Skeleton className="h-20 w-full" />
                </div>
              </div>
            </CardHeader>
          </Card>
        </div>
      </div>
    );
  }

  if (notFound || !feedback) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-2xl font-bold mb-4">Feedback Not Found</h1>
          <p className="text-muted-foreground mb-6">
            The feedback item you're looking for doesn't exist or has been removed.
          </p>
          <Button asChild>
            <Link to="/feedback">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Feedback
            </Link>
          </Button>
        </div>
      </div>
    );
  }

  const canEdit = isAuthenticated && user?.id === feedback.author_id && !feedback.is_published;

  return (
    <>
      <Helmet>
        <title>{feedback.title} - Feedback - Light Hunt</title>
        <meta name="description" content={feedback.description || `Feedback: ${feedback.title}`} />
        <meta name="keywords" content={`feedback, ${feedback.category.name}, Light Hunt`} />
        <link rel="canonical" href={`${window.location.origin}/feedback/${feedback.slug}`} />
        
        {/* Open Graph */}
        <meta property="og:title" content={`${feedback.title} - Light Hunt Feedback`} />
        <meta property="og:description" content={feedback.description || `Feedback: ${feedback.title}`} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={`${window.location.origin}/feedback/${feedback.slug}`} />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={`${feedback.title} - Light Hunt Feedback`} />
        <meta name="twitter:description" content={feedback.description || `Feedback: ${feedback.title}`} />
      </Helmet>

      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto space-y-6">
          {/* Back Navigation */}
          <Button variant="ghost" asChild className="gap-2">
            <Link to="/feedback">
              <ArrowLeft className="h-4 w-4" />
              Back to Feedback
            </Link>
          </Button>

          {/* Main Feedback Card */}
          <Card>
            <CardHeader>
              <div className="flex items-start gap-4">
                {/* Voting Component */}
                <div className="shrink-0">
                  <VotingComponent
                    feedbackId={feedback.id}
                    initialVoteCount={feedback.vote_count}
                    initialUserVote={feedback.user_vote}
                    size="lg"
                  />
                </div>

                {/* Main Content */}
                <div className="flex-1 min-w-0">
                  {/* Badges */}
                  <div className="flex flex-wrap items-center gap-2 mb-3">
                    <Badge
                      variant="secondary"
                      style={{ backgroundColor: `${feedback.category.color}15`, color: feedback.category.color }}
                      className="font-medium"
                    >
                      {feedback.category.name}
                    </Badge>

                    {feedback.status.is_public && (
                      <Badge
                        variant="outline"
                        style={{ borderColor: feedback.status.color, color: feedback.status.color }}
                      >
                        {feedback.status.name}
                      </Badge>
                    )}

                    {feedback.is_featured && (
                      <Badge variant="default" className="bg-yellow-500 hover:bg-yellow-600">
                        Featured
                      </Badge>
                    )}

                    {feedback.has_attachments && (
                      <Badge variant="outline" className="gap-1">
                        <Paperclip className="h-3 w-3" />
                        Attachments
                      </Badge>
                    )}
                  </div>

                  {/* Title */}
                  <h1 className="text-2xl font-bold mb-4 leading-tight">
                    {feedback.title}
                  </h1>

                  {/* Description */}
                  {feedback.description && (
                    <div className="prose prose-sm max-w-none mb-4">
                      <p className="text-muted-foreground whitespace-pre-wrap">
                        {feedback.description}
                      </p>
                    </div>
                  )}

                  {/* Attachments */}
                  {feedback.attachment_urls && feedback.attachment_urls.length > 0 && (
                    <div className="space-y-2 mb-4">
                      <h3 className="font-medium text-sm">Attachments</h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {feedback.attachment_urls.map((url, index) => (
                          <a
                            key={index}
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 p-2 border rounded-lg hover:bg-muted/50 transition-colors"
                          >
                            <Paperclip className="h-4 w-4 text-muted-foreground" />
                            <span className="text-sm truncate">Attachment {index + 1}</span>
                            <ExternalLink className="h-3 w-3 text-muted-foreground ml-auto" />
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex flex-col gap-2">
                  <Button variant="outline" size="sm" onClick={handleShare}>
                    <Share2 className="h-4 w-4" />
                  </Button>
                  
                  {canEdit && (
                    <>
                      <Button variant="outline" size="sm">
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button variant="outline" size="sm" className="text-destructive hover:text-destructive">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </>
                  )}
                  
                  <Button variant="outline" size="sm" className="text-muted-foreground">
                    <Flag className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
          </Card>

          {/* Metadata */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex flex-wrap items-center gap-6 text-sm text-muted-foreground">
                {/* Author */}
                <div className="flex items-center gap-2">
                  <Avatar className="h-6 w-6">
                    <AvatarImage src={feedback.author_profile?.avatar_url} />
                    <AvatarFallback className="text-xs">
                      {getInitials(feedback.author_profile?.full_name || feedback.author_profile?.username)}
                    </AvatarFallback>
                  </Avatar>
                  <span>by {feedback.author_profile?.username || 'Anonymous'}</span>
                </div>

                <Separator orientation="vertical" className="h-4" />

                {/* Created Date */}
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  <span>{formatDate(feedback.created_at)}</span>
                </div>

                <Separator orientation="vertical" className="h-4" />

                {/* Comments */}
                <div className="flex items-center gap-2">
                  <MessageCircle className="h-4 w-4" />
                  <span>{feedback.comment_count} {feedback.comment_count === 1 ? 'comment' : 'comments'}</span>
                </div>

                {/* Updated Date */}
                {feedback.updated_at !== feedback.created_at && (
                  <>
                    <Separator orientation="vertical" className="h-4" />
                    <span>Updated {formatDate(feedback.updated_at)}</span>
                  </>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Comments Section */}
          <Card>
            <CardHeader>
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <MessageCircle className="h-5 w-5" />
                Discussion ({feedback.comment_count})
              </h2>
            </CardHeader>
            <CardContent>
              <div className="text-center py-8 text-muted-foreground">
                <MessageCircle className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>Comments system will be implemented in the next phase</p>
                <p className="text-sm">This will include threaded discussions and real-time updates</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
};

export default FeedbackDetail;